// 3rd-party modules
import { useSearchParams } from 'react-router-dom';
import { message, Steps } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

// project modules
import AccountDevice from "./AccountDevice";
import AccountLocationForm from "../accounts/locations/AccountLocationForm";
import AccountPayment from "./AccountPayment";
import AccountSubscription from "./AccountSubscription";
import ConfirmationPopup from "../shared/popup/confirmationPopup";
import Loader from "../shared/loader";
import { apiCall } from "../../helpers/apiHelper";
import { RootState } from "../../stores";

// apis
import * as AccountLocationApi from '../../apis/accountLocationApi';
import * as AccountSubscriptionApi from '../../apis/accountSubscriptionApi';

// models
import { AccountKiboshDevice } from "../../models/accountKiboshDevice";
import { AccountLocation } from "../../models/accountLocation";
import { Product } from "../../models/product";
import { User } from "../../models/auth";

type Props = {
  refresh?: boolean;
  hasSignup?: boolean;
  hasBackButtonOnFirstStep?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
};

export default function CreateNewSubscription({ refresh, hasSignup = true, hasBackButtonOnFirstStep = true, onCancel, onSave }: Props) {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [currentSubscriptions, setCurrentSubscriptions] = useState<any[]>([]);
  const [currentMode, setCurrentMode] = useState<string | null>(null);
  const [loadingLocationsList, setLoadingLocationsList] = useState(false);
  const [loadingSubscribtion, setLoadingSubscribtion] = useState(false);
  const [isCancelSubConfirmationPopupOpen, setIsCancelSubConfirmationPopupOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<AccountKiboshDevice | null>(null);
  const [selectedBillingLocation, setSelectedBillingLocation] = useState<AccountLocation | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedDeviceType, setSelectedDeviceType] = useState<any>(null);
  const [isSelectedDeviceFromOldDevices, setIsSelectedDeviceFromOldDevices] = useState<boolean>(false);
  const [steps, setSteps] = useState<any[]>([
    {
      id: 0,
      title: "Sign Up"
    },
    {
      id: 1,
      title: "Kibosh Device",
    },
    {
      id: 2,
      title: "Billing Address",
    },
    {
      id: 3,
      title: "Kibosh Subscription",
    },
    {
      id: 4,
      title: "Payment",
    },
  ]);

  const abortController = new AbortController();

  const user = useSelector((state: RootState) => state.user.currentUser) as User;
  const [searchParams] = useSearchParams();

  useEffect(() => {
    getBillingLocationAsync();
    const mode = searchParams.get("mode");

    if (mode?.toLowerCase() === "vpn" || mode?.toLowerCase() === "dns") {
      setCurrentMode(mode.toLowerCase())
      setSelectedDeviceType( mode.toLowerCase() )
    }
  }, []);

  useEffect(() => {
    const deviceStep = hasSignup ? 1 : 0;
    if (currentStep > 1 && !selectedDevice?.kiboshDeviceReference) {
      message.error("Kibosh Device is not entered.");
      setCurrentStep(deviceStep);
    }
  }, [currentStep]);

  const getBillingLocationAsync = async () => {
    let visibleSteps = [...steps];
    if (!hasSignup) {
      visibleSteps = visibleSteps.filter(x => x.id !== 0);
      setSteps(visibleSteps);
      setCurrentStep(0);
    }

    const locations = await getAccountLocations(abortController.signal);
    const billing = locations.filter(x => x.billingAddress);

    if (billing.length) {
      setSelectedBillingLocation(billing[0]);
      // setSteps(steps.filter(x => x.id !== 2));
      visibleSteps = visibleSteps.filter(x => x.id !== 2);
      setSteps(visibleSteps);
    }
  }

  const getAccountLocations = async (abortSignal?: AbortSignal) => {
    setLoadingLocationsList(true);
    const response = await apiCall(AccountLocationApi.getAccountLocations(user?.accountId!, {}, abortSignal));
    setLoadingLocationsList(false);

    return response.success ? AccountLocation.toArrayOfClass(response.data?.value || []) : [];
  }

  const getKiboshDeviceActiveSubscription = async (kiboshDeviceReference: string) => {
    setLoadingSubscribtion(true);
    const response = await apiCall(AccountSubscriptionApi.getKiboshDeviceActiveSubscription(kiboshDeviceReference, abortController.signal));
    setLoadingSubscribtion(false);

    return response.success ? response.data?.value || [] : [];
  };

  const hasStep = (id: number): boolean => {
    return steps.some(step => step.id === id);
  }

  const onStepCancel = () => {
    if (hasSignup) {
      switch (currentStep) {
        case 1:
          if (onCancel) onCancel();
          break;
        case 2:
          setCurrentStep(1);
          break;
        case 3:
          setCurrentStep(2)
          break;
        case 4: // Strip logic here
          setCurrentStep(3);
          break;
      }
    } else {
      switch (currentStep) {
        case 0:
          if (onCancel) onCancel();
          break;
        case 1:
          setCurrentStep(0);
          break;
        case 2:
          setCurrentStep(1)
          break;
        case 3: // Strip logic here
          setCurrentStep(2);
          break;
      }
    }
  };

  const onStepSave = async (value: any, step: string) => {
    if(hasSignup) {
      switch (step) {
        case "device":
          setSelectedDevice(value.device);
          setSelectedDeviceType(value.type);
          setIsSelectedDeviceFromOldDevices(value.isSelectedKiboshDevice);
          const data = await getKiboshDeviceActiveSubscription(value.device.kiboshDeviceReference);
          if (data.lenght) {
            setCurrentSubscriptions(data);
            setIsCancelSubConfirmationPopupOpen(true);
          } else {
            setCurrentStep(2);
          }
          break;
        case "location":
          setSelectedBillingLocation(value);
          setCurrentStep(3)
          break;
        case "subscription":
          setSelectedProduct(value);
          setCurrentStep(hasStep(2) ? 4 : 3)
          break;
        case "payment": // Strip logic here
          break;
      }
    } else {
      switch (step) {
        case "device":
          setSelectedDevice(value.device);
          setSelectedDeviceType(value.type);
          setIsSelectedDeviceFromOldDevices(value.isSelectedKiboshDevice);
          const data = await getKiboshDeviceActiveSubscription(value.device.kiboshDeviceReference);
          if (data.lenght) {
            setCurrentSubscriptions(data);
            setIsCancelSubConfirmationPopupOpen(true);
          } else {
            setCurrentStep(1);
          }
          break;
        case "location":
          setSelectedBillingLocation(value);
          setCurrentStep(2)
          break;
        case "subscription":
          setSelectedProduct(value);
          setCurrentStep(hasStep(2) ? 3 : 2)
          break;
        case "payment": // Strip logic here
          break;

      }
    }
  };

  const handleCancelSubscriptionRequest = async () => {
    setLoadingSubscribtion(true);
    Promise.all(currentSubscriptions.map((x: any) => handleCancelRequest(x))).then(
      (results: any) => {
        setLoadingSubscribtion(false);
        setIsCancelSubConfirmationPopupOpen(false);
        setCurrentStep(2);
      }
    );
  };

  const handleCancelRequest = async (data: any) => {
    const response = await apiCall(AccountSubscriptionApi.cancelSubscription(data));
    return response.success;
  };

  const modeToDeviceType = (mode:any, deviceType:any) => {
    mode = mode ? mode.toLowerCase() : "" 
    return (mode == "vpn" || mode == "dns" ? mode : selectedDeviceType  )
  };

  return (
    <>
      {(loadingLocationsList || loadingSubscribtion) && <Loader />}
      <view data-scroll="" data-border="none"  data-margin-bottom="100">
        <section>
          <wrapper>
            <group>
              <group>
                <space data-height="40"></space>
                <Steps
                  current={currentStep}
                  items={steps}
                />
              </group>
              <group style={{ display: "none" }}><text>{ selectedDeviceType }</text></group>
              {((hasSignup && currentStep === 1) || (!hasSignup && currentStep === 0)) && (
                <AccountDevice
                  accountId={user.accountId}
                  showCancelButton={hasBackButtonOnFirstStep}
                  autoCreateDeviceMode={currentMode}
                  isSelectedKiboshDevice={isSelectedDeviceFromOldDevices}
                  cancelText="Back To Login"
                  okText="Save & Next"
                  selectedDevice={selectedDevice}
                  onCancel={onStepCancel}
                  onSave={(value: any) => onStepSave(value, "device")}
                />
              )}
              {((hasSignup && currentStep === 2) || (!hasSignup && currentStep === 1)) && hasStep(2) && (
                <AccountLocationForm
                  refresh={true}
                  cancelText="Back"
                  okText="Save & Next"
                  accountId={user.accountId!}
                  accountLocation={new AccountLocation()}
                  shouldAddBilling={true}
                  isFromSignup={true}
                  onClose={onStepCancel}
                  onSave={(value: any) => onStepSave(value, "location")} />
              )}
              {((((hasSignup && currentStep === 3) || (!hasSignup && currentStep === 2)) && hasStep(2)) ||
                (((hasSignup && currentStep === 2) || (!hasSignup && currentStep === 1)) && !hasStep(2))) && (
                <AccountSubscription
                  accountId={user.accountId}
                  deviceType={ modeToDeviceType( searchParams.get("mode"), selectedDeviceType) }
                  cancelText="Back"
                  okText="Save & Next"
                  onCancel={onStepCancel}
                  onSave={(value: any) => onStepSave(value, "subscription")}
                />
              )}
              {((((hasSignup && currentStep === 4) || (!hasSignup && currentStep === 3)) && hasStep(2)) ||
                (((hasSignup && currentStep === 3) || (!hasSignup && currentStep === 2)) && !hasStep(2))) && (
                <AccountPayment
                  account={user}
                  selectedDevice={selectedDevice}
                  selectedBillingLocation={selectedBillingLocation}
                  selectedProduct={selectedProduct}
                  cancelText="Back"
                  okText="Payment"
                  onCancel={onStepCancel}
                  onSave={(value: any) => onStepSave(value, "payment")}
                />
              )}
            </group>
          </wrapper>
        </section>
      </view>

      {isCancelSubConfirmationPopupOpen && (
        <ConfirmationPopup
          message="This Kibosh device has active subscription, you should cancel that before Continue."
          showButton={false}
          positiveButtonText="Cancel current subscribtion and Continue"
          positiveCallback={handleCancelSubscriptionRequest}
          negativeCallback={() => {
            setIsCancelSubConfirmationPopupOpen(false);
          }}
        />
      )}
    </>
  );
}
